// Editable numeric cell — single-input design. Always an <input>, never
// a focus-swap from a Number primitive. type="text" + inputMode="decimal"
// suppresses the browser's number-input spinner arrows while still giving
// mobile keyboards the right layout. At rest the input shows the formatted
// value (e.g. "1,234.5678"); on focus we swap to the raw editable string
// (e.g. "1234.5678") and select-all so users can immediately overtype.
import { CSSProperties, KeyboardEvent, ReactElement, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { NumberFormat, NumberRole, NumberColour, formatValue } from '../number';

export interface EditableNumberProps {
  value: number | null;
  onCommit: (next: number) => void;
  format: NumberFormat;
  role?: NumberRole;
  colour?: NumberColour;
  placeholder?: string;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  ariaLabel: string;
  // Interpret a trailing k/m suffix as a thousands/millions multiplier
  // (e.g. "10.4m" -> 10400000, "500k" -> 500000). Opt-in per field — only
  // makes sense for notional-style entry, not e.g. strikes/barriers that
  // happen to share the same NumberFormat.
  allowUnitSuffix?: boolean;
  // Explicit tab stop position — see trade-cells.tsx's tabIndexFor(). Omit
  // for natural DOM-order tabbing.
  tabIndex?: number;
}

// AT-REST display formatting is shared with the Number primitive via the
// exported `formatValue` (single source of truth — see ../number). The only
// difference here is that we feed its string straight into an <input value=...>.

// Format for EDIT mode — raw number string the user can type-overwrite.
// Strips commas/percent/units so a re-parse round-trips cleanly.
function formatForEdit(value: number | null, format: NumberFormat): string {
  if (value === null || !Number.isFinite(value)) return '';
  // For percent / signed-percent we keep the user editing the decimal
  // form (matches how the engine and contracts.ts store these values).
  if (format === 'percent' || format === 'signed-percent') {
    return String(value);
  }
  if (format === 'bps') {
    return String(value * 10000); // edit as bps
  }
  return String(value);
}

const UNIT_SUFFIX_MULTIPLIERS: Record<string, number> = { k: 1e3, m: 1e6 };

function parseEdit(
  raw: string,
  format: NumberFormat,
  min?: number,
  max?: number,
  allowUnitSuffix?: boolean,
): number | null {
  let trimmed = raw
    .trim()
    .replace(/,/g, '')
    .replace('%', '')
    .replace(/\s*bps$/i, '');
  if (trimmed === '') return null;

  let unitMultiplier = 1;
  if (allowUnitSuffix) {
    const suffixMatch = trimmed.match(/^(-?[\d.]+)\s*([km])$/i);
    if (suffixMatch) {
      trimmed = suffixMatch[1];
      unitMultiplier = UNIT_SUFFIX_MULTIPLIERS[suffixMatch[2].toLowerCase()];
    }
  }

  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  // Bring bps edits back into the decimal storage form.
  const scaled = (format === 'bps' ? n / 10000 : n) * unitMultiplier;
  if (min !== undefined && scaled < min) return null;
  if (max !== undefined && scaled > max) return null;
  return scaled;
}

const colourFor = (colour: NumberColour | undefined, theme: Theme): string => {
  switch (colour) {
    case 'positive':
      return theme.palette.success.main;
    case 'negative':
      return theme.palette.error.main;
    case 'accent':
      return theme.palette.primary.main;
    case 'muted':
      return theme.palette.text.secondary;
    default:
      return theme.palette.text.primary;
  }
};

const sizeFor = (role: NumberRole | undefined): CSSProperties => {
  switch (role) {
    case 'headline':
      return { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.2 };
    case 'caption':
      return { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 };
    case 'small':
      return { fontSize: '0.6875rem', fontWeight: 400, lineHeight: 1.4 };
    case 'micro':
      return { fontSize: '0.625rem', fontWeight: 400, lineHeight: 1.3 };
    case 'body':
    default:
      return { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.4 };
  }
};

const EditableNumber = ({
  value,
  onCommit,
  format,
  role,
  colour,
  placeholder = '—',
  className,
  min,
  max,
  step,
  disabled,
  ariaLabel,
  allowUnitSuffix,
  tabIndex,
}: EditableNumberProps): ReactElement => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelling = useRef(false);

  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string>('');
  const [invalid, setInvalid] = useState(false);

  // The single displayed text: at rest it's the formatted at-rest text
  // (or placeholder when null); focused, the raw editable string the user
  // is typing.
  const displayText = useMemo(() => {
    if (focused) return draft;
    if (value === null || !Number.isFinite(value)) return placeholder;
    return formatValue(format, value);
  }, [focused, draft, value, format, placeholder]);

  const onFocus = () => {
    if (disabled) return;
    setDraft(formatForEdit(value, format));
    setInvalid(false);
    setFocused(true);
    // Defer select-all until after the value swap renders so the new
    // editable string is what gets selected.
    requestAnimationFrame(() => {
      inputRef.current?.select();
    });
  };

  const tryCommit = (raw: string) => {
    const parsed = parseEdit(raw, format, min, max, allowUnitSuffix);
    if (parsed === null) {
      setInvalid(true);
      return false;
    }
    setInvalid(false);
    onCommit(parsed);
    return true;
  };

  const onBlur = () => {
    if (cancelling.current) {
      cancelling.current = false;
      setInvalid(false);
      setFocused(false);
      return;
    }
    tryCommit(draft);
    setFocused(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Prevent default first: if this input sits inside a <form> (or
      // any ancestor with its own Enter handling), the browser's native
      // Enter behaviour (form submit) can interfere with the manual
      // blur commit below. Let blur handle commit on Enter/Tab/blur
      // (architect E3) — Tab is deliberately not handled here, since it
      // already blurs (moving focus to the next element) as native
      // default behaviour, and calling blur() ourselves too raced
      // against that native focus change in a way that could drop the
      // commit.
      e.preventDefault();
      inputRef.current?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelling.current = true;
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      spellCheck={false}
      className={className}
      value={displayText}
      step={step}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      tabIndex={tabIndex}
      onChange={(e) => {
        setDraft(e.target.value);
        if (invalid) setInvalid(false);
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      style={{
        background: 'transparent',
        border: 'none',
        // No focus underline — caret + selection are the focus affordance.
        // Invalid state still shows a red underline because that's load-bearing
        // feedback the user needs to see.
        borderBottom: invalid ? `1px solid ${theme.palette.error.main}` : '1px solid transparent',
        color: colourFor(colour, theme),
        fontFamily: 'Inter',
        fontVariantNumeric: 'tabular-nums',
        outline: 'none',
        padding: '1px 2px',
        width: '100%',
        textAlign: 'right',
        cursor: disabled ? 'not-allowed' : 'text',
        opacity: disabled ? 0.5 : 1,
        ...sizeFor(role),
      }}
    />
  );
};

export default EditableNumber;
